import type {SuggestedLabel} from '../api/memex-items/contracts'
import {filterSuggestions, type FuzzyFilterData, type FuzzyFilterPositionData, ScoringCache} from './suggester'

const scoringCache = new ScoringCache<SuggestedLabel>()
const scoreFn = scoringCache.sortByWithCache.bind(scoringCache)

const getNameHtml = (item: SuggestedLabel) => item.nameHtml

/**
 * Given filter text from the labels select menu and a list of all suggested labels from the server,
 * this function filters and sorts that listbased on the call to fzy.js (https://github.com/jhawthorn/fzy.js/).
 * @param searchQuery current filter text
 * @param suggestionsData all items to filter and prioritize
 * @param maxSuggestions maximum number of suggestions to return
 * @returns A filtered, prioritized and limited list of labels.
 */
export function filterSuggestedLabels(
  searchQuery: string,
  suggestionsData: Array<SuggestedLabel>,
  maxSuggestions: number,
): FuzzyFilterData<SuggestedLabel> {
  return filterSuggestions(searchQuery, suggestionsData, getNameHtml, scoringCache, scoreFn, maxSuggestions)
}

/**
 * The suggestions endpoint caps its response at 5 across all reasons. Applying the
 * same cap here keeps the group bounded even if the server limit is raised later.
 */
const MAX_SUGGESTED_LABELS = 5

/**
 * A suggested label that is known to carry the server's merged-list position.
 *
 * Partitioning proves the field is present, but that narrowing is lost across
 * the generic `filterSuggestions` boundary, so it is carried in the type
 * instead. Without it the sort would need a fallback, and any fallback is wrong
 * here: a default of `0` would promote an unordered label to the highest-priority
 * slot in the group.
 */
type OrderedSuggestion = SuggestedLabel & {suggestionOrder: number}

function hasSuggestionOrder(label: SuggestedLabel): label is OrderedSuggestion {
  return label.suggestionOrder != null
}

/**
 * Filters labels while preserving the server's suggestion ordering.
 *
 * Suggested labels carry a server-assigned position that reflects source
 * precedence and how the viewer actually uses them. Running them through the
 * same pass as every other label would destroy that in two ways: fzy re-sorts
 * matches by score, and the shared result cap (12) can drop a highly ranked
 * suggestion entirely when many labels sort ahead of it. Both are silent — the
 * group would simply come back in the wrong order or short.
 *
 * So suggestions are partitioned out *before* filtering, filtered against their
 * own cap, and then restored to the server's order. fzy decides only which
 * suggestions match; the server decides how they are ordered.
 *
 * Ordering keys off `suggestionOrder`, not `suggestionRank`: rank restarts at 0
 * for each reason, so sorting by it would interleave the reasons instead of
 * keeping the server's precedence (all "Recent", then all "Popular").
 */
export function filterLabelsPreservingSuggestionRank(
  searchQuery: string,
  suggestionsData: Array<SuggestedLabel>,
  maxSuggestions: number,
): FuzzyFilterData<SuggestedLabel> {
  const suggested: Array<OrderedSuggestion> = []
  const rest: Array<SuggestedLabel> = []

  for (const label of suggestionsData) {
    if (hasSuggestionOrder(label)) {
      suggested.push(label)
    } else {
      rest.push(label)
    }
  }

  if (suggested.length === 0) {
    return filterSuggestedLabels(searchQuery, suggestionsData, maxSuggestions)
  }

  const suggestedResult = filterSuggestions<OrderedSuggestion>(
    searchQuery,
    suggested,
    getNameHtml,
    scoringCache,
    scoreFn,
    MAX_SUGGESTED_LABELS,
  )
  const restResult = filterSuggestions(searchQuery, rest, getNameHtml, scoringCache, scoreFn, maxSuggestions)

  const rankedSuggestions = [...suggestedResult.filteredItems].sort((a, b) => a.suggestionOrder - b.suggestionOrder)

  const positionData = new WeakMap<SuggestedLabel, FuzzyFilterPositionData>()
  for (const item of rankedSuggestions) {
    const data = suggestedResult.positionData.get(item)
    if (data) positionData.set(item, data)
  }
  for (const item of restResult.filteredItems) {
    const data = restResult.positionData.get(item)
    if (data) positionData.set(item, data)
  }

  return {filteredItems: [...rankedSuggestions, ...restResult.filteredItems], positionData}
}
